<script lang='ts'>
import type { ComponentSize } from 'element-plus'
import type { PropType } from 'vue'
import * as RA from 'ramda-adjunct'
import { computed, defineComponent, nextTick, ref, watch } from 'vue'

export default defineComponent({
  name: 'MsInput',
  props: {
    outlined: {
      type: Boolean,
      default: true,
    },
    standout: {
      type: Boolean,
      default: false,
    },
    fill: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    modelModifiers: {
      type: Object as PropType<Partial<{
        trim: boolean
        number: boolean
        lazy: boolean
      }>>,
      default: () => ({} as Partial<{
        trim: boolean
        number: boolean
        lazy: boolean
      }>),
    },
    type: {
      type: String,
      default: 'text',
    },
    size: {
      type: String as PropType<ComponentSize>,
      validator: (val: string) =>
        ['', 'default', 'small', 'large'].includes(val),
    },
    resize: {
      type: String as PropType<'none' | 'both' | 'horizontal' | 'vertical'>,
      validator: (val: string) =>
        ['none', 'both', 'horizontal', 'vertical'].includes(val),
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    placeholder: {
      type: String,
    },
    form: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    showPassword: {
      type: Boolean,
      default: false,
    },
    showWordLimit: {
      type: Boolean,
      default: false,
    },
    suffixIcon: {
      type: String,
      default: '',
    },
    prefixIcon: {
      type: String,
      default: '',
    },
    label: {
      type: String,
    },
    tabindex: {
      type: [Number, String],
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    maxlength: {
      type: [Number, String],
    },
    formatter: {
      type: Function as PropType<(value: any) => string>,
    },
    parser: {
      type: Function as PropType<(value: string) => any>,
    },
  },
  emits: ['input', 'change', 'blur', 'focus', 'clear', 'update:modelValue'],
  setup(props, { emit }) {
    const input = ref()
    const pwdVisible = computed(() => input.value?.passwordVisible)
    const extClass = computed(() => {
      if (!props.showPassword) {
        return ''
      }
      return `pwd-hook ${pwdVisible.value ? 'pwd-visible' : 'pwd-invisible'}`
    })

    const inputValue = ref(format(props.modelValue))
    watch(() => props.modelValue, (newVal) => {
      if (same(format(newVal))) {
        return
      }
      inputValue.value = format(newVal)
    })

    function same(newVal) {
      if (props.type === 'number') {
        return RA.isNumber(newVal) && RA.isNumber(inputValue.value) && (Number(newVal) - Number(inputValue.value) === 0)
      }
      return newVal == inputValue.value
    }

    function format(value) {
      if (props.formatter) {
        value = props.formatter(value)
      }
      return value
    }

    function parse(value) {
      if (props.parser) {
        value = props.parser(value)
      }
      return value
    }

    async function updateVal(newVal: string) {
      let v: string | number = newVal
      if (props.maxlength && props.type === 'number') {
        const maxL = Number(props.maxlength)
        const str = String(v)
        if (str.length > maxL) {
          v = str.substr(0, maxL)
        }
      }
      if (props.modelModifiers?.trim) {
        v = v.trim()
      }
      if (props.modelModifiers?.number) {
        const n = Number.parseFloat(v)
        if (!Number.isNaN(n)) {
          v = n
        }
      }
      const parsedValue = parse(v)
      emit('update:modelValue', parsedValue)
      return parsedValue
    }

    function handleInput(e) {
      if (!props.modelModifiers?.lazy) {
        updateVal(e).then(async (res) => {
          emit('input', res)
          await nextTick()
          inputValue.value = format(res)
        })
      }
    }

    function handleChange(e) {
      updateVal(e).then(async (res) => {
        emit('change', res)
        await nextTick()
        inputValue.value = format(res)
      })
    }

    function handleClear() {
      updateVal('').then(() => emit('clear'))
    }

    function handleBlur(e) {
      emit('blur', e)
    }

    return {
      input,
      extClass,
      inputValue,
      handleInput,
      handleChange,
      handleClear,
      handleBlur,
    }
  },
})
</script>

<template>
  <div :class="[fill ? `fill` : standout ? 'standout' : '']" style="width: 100%">
    <el-input
      v-bind="$attrs" ref="input" v-model="inputValue"
      :type="type" :size="size" :resize="resize" :autocomplete="autocomplete"
      :placeholder="placeholder" :form="form" :disabled="disabled" :readonly="readonly"
      :clearable="clearable" :show-password="showPassword" :show-word-limit="showWordLimit"
      :suffix-icon="suffixIcon" :prefix-icon="prefixIcon" :label="label" :tabindex="tabindex"
      :validate-event="validateEvent" :maxlength="maxlength"
      :class="[extClass, fill ? `fill` : '']" @blur="handleBlur"
      @focus="$emit('focus', $event)" @input="handleInput" @change="handleChange" @clear="handleClear"
    >
      <template v-if="!!$slots.prefix" #prefix>
        <slot name="prefix" />
      </template>
      <template v-if="!!$slots.suffix" #suffix>
        <slot name="suffix" />
      </template>
      <template v-if="!!$slots.prepend" #prepend>
        <slot name="prepend" />
      </template>
      <template v-if="!!$slots.append" #append>
        <slot name="append" />
      </template>
    </el-input>
  </div>
</template>

<style lang='scss'>
.pwd-hook {
  ::v-deep(.el-icon-view) {
    position: relative;
  }

  &.pwd-visible {
    ::v-deep(.el-icon-view) {
      &::after {
        content: '';
        position: absolute;
        width: 1px;
        height: 16px;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        transform-origin: 50%;
        transform: rotate(45deg);
        background: $font-color-place-holder;
      }
    }
  }
}

.el-form-item.is-error {
  .standout,
  .fill {
    .el-input__wrapper {
      box-shadow: none !important;
      border-bottom: 1px solid var(--el-color-danger);

      &.is-focus,
      &:hover,
      &:active {
        box-shadow: none !important;
        border-bottom: 1px solid var(--el-color-danger);
      }
    }
  }
}

.standout,
.fill {
  .el-input {
    --el-disabled-bg-color: transparent;
    --el-disabled-border-color: transparent;
    --el-input-border-color: transparent;
    --el-input-focus-border-color: transparent;
    --el-input-hover-border-color: transparent;
    --el-hover-border-color: transparent;
  }
}

.standout {
  .el-input__wrapper {
    border-radius: unset;
    padding-left: 0;
    border-bottom: 1px solid var(--el-border-color);

    &.is-focus {
      border-bottom: 1px solid var(--el-input-focus-border);
    }
  }

  .el-textarea {
    .el-textarea__inner {
      padding: 10px 15px 25px 15px;
    }
  }
}
</style>
